import { useState, useEffect } from 'react'
import Alert from '@cloudscape-design/components/alert'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Header from '@cloudscape-design/components/header'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Spinner from '@cloudscape-design/components/spinner'
import Table from '@cloudscape-design/components/table'
import Tabs from '@cloudscape-design/components/tabs'
import { useAppStore } from '@/store/app-store'
import { resultsStore } from '@/engine/results-store'
import { SummaryTable } from '@/components/dashboard/SummaryTable'
import { WaterfallChart } from '@/components/dashboard/WaterfallChart'
import { HistogramPanel } from '@/components/dashboard/HistogramPanel'
import { AnalysisPanel } from '@/components/dashboard/AnalysisPanel'
import { ChatPanel } from '@/components/dashboard/ChatPanel'
import { NetworkWaterfall } from '@/components/diagnostics/NetworkWaterfall'
import { ClientLatencyPanel } from '@/components/diagnostics/ClientLatencyPanel'
import { exportJson, exportCsv } from '@/engine/export'
import { getLatencyColor } from '@/utils/latency-colors'
import type { BenchmarkRun } from '@/types'

const RUNS_KEY = 'bedrock-diag-runs'

interface Props {
  runId: string
  onBack: () => void
}

// ── Findings generator (reused from ComparisonDashboard) ────────────────

function generateFindings(run: BenchmarkRun): string[] {
  const findings: string[] = []
  const { stats } = run
  if (stats.length < 2) return ['Run benchmarks with multiple profiles to see comparisons.']

  const sorted = [...stats].sort((a, b) => a.medianTotalSec - b.medianTotalSec)
  const fastest = sorted[0]
  const slowest = sorted[sorted.length - 1]

  if (fastest && slowest && fastest.profileId !== slowest.profileId) {
    const diff = slowest.medianTotalSec - fastest.medianTotalSec
    findings.push(`${slowest.profileName} is ${diff.toFixed(2)}s slower (median) than ${fastest.profileName}`)
  }

  const withGR = stats.filter((s) => s.avgGuardrailTotalMs > 0)
  const withoutGR = stats.filter((s) => s.avgGuardrailTotalMs === 0 && s.profileType !== 'anthropic-direct')
  if (withGR.length > 0 && withoutGR.length > 0) {
    const avgGRLatency = withGR.reduce((a, s) => a + s.avgGuardrailTotalMs, 0) / withGR.length
    findings.push(`Guardrails add ${Math.round(avgGRLatency)}ms average total latency (input + output)`)
  }

  const bestThroughput = Math.max(...stats.map((s) => s.avgThroughput))
  const worstThroughput = Math.min(...stats.filter((s) => s.avgThroughput > 0).map((s) => s.avgThroughput))
  if (bestThroughput > 0 && worstThroughput > 0 && bestThroughput / worstThroughput > 1.5) {
    findings.push(`Throughput varies ${(bestThroughput / worstThroughput).toFixed(1)}x across profiles`)
  }

  return findings
}

// ── Network Diagnostics Tab (reused logic from ComparisonDashboard) ─────

function NetworkTab({ run }: { run: BenchmarkRun }) {
  const networkProbes = useAppStore((s) => s.networkProbes)
  // Prefer probes stored on the run itself, fall back to global store
  const probes = run.networkProbes.length > 0 ? run.networkProbes : networkProbes
  const { stats } = run

  const overheadData = stats
    .filter((s) => s.avgBedrockFirstByteMs > 0 || s.avgBedrockInvocationMs > 0)
    .map((s) => {
      const clientTtftMs = Math.round(s.avgTtftSec * 1000)
      const serverTtftMs = Math.round(s.avgBedrockFirstByteMs)
      const clientTotalMs = Math.round(
        s.avgE2eTotalSec > 0 ? s.avgE2eTotalSec * 1000 : s.avgTotalSec * 1000,
      )
      const serverTotalMs = Math.round(
        s.avgBedrockInvocationMs > 0 ? s.avgBedrockInvocationMs : s.avgTotalSec * 1000,
      )
      return {
        profile: s.profileName,
        clientTtftMs,
        serverTtftMs,
        ttftOverheadMs: serverTtftMs > 0 ? clientTtftMs - serverTtftMs : 0,
        clientTotalMs,
        serverTotalMs,
        e2eOverheadMs: serverTotalMs > 0 ? clientTotalMs - serverTotalMs : 0,
        hasServerTtft: serverTtftMs > 0,
        hasServerTotal: serverTotalMs > 0,
        bedrockInvocationMs: Math.round(s.avgBedrockInvocationMs),
      }
    })

  if (probes.length === 0 && overheadData.length === 0) {
    return (
      <Box textAlign="center" color="text-body-secondary" padding="l">
        Network probes run automatically during benchmarks. No data available for this run.
      </Box>
    )
  }

  return (
    <SpaceBetween size="l">
      <NetworkWaterfall probes={probes} />
      <ClientLatencyPanel results={run.results} probes={probes} />
      {overheadData.length > 0 && (
        <Table
          columnDefinitions={[
            {
              id: 'profile',
              header: 'Profile',
              cell: (item: (typeof overheadData)[0]) => item.profile,
            },
            {
              id: 'clientTtft',
              header: 'Client TTFT',
              cell: (item: (typeof overheadData)[0]) =>
                item.hasServerTtft ? `${item.clientTtftMs}ms` : '-',
            },
            {
              id: 'serverTtft',
              header: 'Server TTFT',
              cell: (item: (typeof overheadData)[0]) =>
                item.hasServerTtft ? `${item.serverTtftMs}ms` : '-',
            },
            {
              id: 'ttftOverhead',
              header: 'TTFT Overhead',
              cell: (item: (typeof overheadData)[0]) => {
                if (!item.hasServerTtft) return '-'
                const pct =
                  item.clientTtftMs > 0
                    ? Math.round((item.ttftOverheadMs / item.clientTtftMs) * 100)
                    : 0
                return (
                  <span style={{ color: getLatencyColor(item.ttftOverheadMs), fontWeight: 600 }}>
                    {item.ttftOverheadMs}ms ({pct}%)
                  </span>
                )
              },
            },
            {
              id: 'e2eOverhead',
              header: 'E2E Overhead',
              cell: (item: (typeof overheadData)[0]) => {
                if (!item.hasServerTotal) return '-'
                const pct =
                  item.clientTotalMs > 0
                    ? Math.round((item.e2eOverheadMs / item.clientTotalMs) * 100)
                    : 0
                return (
                  <span style={{ color: getLatencyColor(item.e2eOverheadMs), fontWeight: 600 }}>
                    {item.e2eOverheadMs}ms ({pct}%)
                  </span>
                )
              },
            },
            {
              id: 'brInvocation',
              header: 'BR Invocation',
              cell: (item: (typeof overheadData)[0]) =>
                item.bedrockInvocationMs > 0 ? `${item.bedrockInvocationMs}ms` : '-',
            },
          ]}
          items={overheadData}
          header={
            <Header description="Client vs server metrics - the gap is your network overhead.">
              Network Overhead
            </Header>
          }
          stripedRows
        />
      )}
      <CallDataTable results={run.results} columns="network" />
    </SpaceBetween>
  )
}

// ── Run loader: localStorage → store → IndexedDB ────────────────────────

function useRunData(runId: string) {
  const currentRun = useAppStore((s) => s.currentRun)
  const pastRuns = useAppStore((s) => s.pastRuns)
  const [run, setRun] = useState<BenchmarkRun | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    // 1. Check Zustand store first (currentRun or pastRuns)
    if (currentRun?.id === runId) {
      setRun(currentRun)
      setLoading(false)
      return
    }
    const fromStore = pastRuns.find((r) => r.id === runId)
    if (fromStore) {
      setRun(fromStore)
      setLoading(false)
      return
    }

    // 2. Check localStorage
    try {
      const raw = localStorage.getItem(RUNS_KEY)
      if (raw) {
        const runs: BenchmarkRun[] = JSON.parse(raw)
        const found = runs.find((r) => r.id === runId)
        if (found) {
          setRun(found)
          setLoading(false)
          return
        }
      }
    } catch {
      /* ignore parse errors */
    }

    // 3. Fall back to IndexedDB
    const loadFromIdb = async () => {
      try {
        const idbRun = await resultsStore.loadRun(runId)
        if (cancelled) return
        if (idbRun) {
          setRun(idbRun)
        } else {
          setError(`Run "${runId}" not found`)
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load run data')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadFromIdb()
    return () => {
      cancelled = true
    }
  }, [runId, currentRun, pastRuns])

  return { run, loading, error }
}

// ── Per-Call Data Table ──────────────────────────────────────────────────

function CallDataTable({ results, columns }: { results: BenchmarkRun['results']; columns: 'summary' | 'charts' | 'network' }) {
  const successful = results.filter((r) => r.success)
  const failed = results.filter((r) => !r.success)

  const summaryColumns = [
    { id: 'call', header: '#', cell: (_: any, i: number) => i + 1, width: 50 },
    { id: 'profile', header: 'Profile', cell: (r: any) => r.profileName },
    { id: 'ttft', header: 'TTFT (ms)', cell: (r: any) => Math.round(r.ttftSec * 1000) },
    { id: 'total', header: 'Total (ms)', cell: (r: any) => Math.round(r.totalTimeSec * 1000) },
    { id: 'throughput', header: 'Tokens/s', cell: (r: any) => r.tokenThroughput?.toFixed(1) ?? '-' },
    { id: 'inputTokens', header: 'In Tokens', cell: (r: any) => r.inputTokens },
    { id: 'outputTokens', header: 'Out Tokens', cell: (r: any) => r.outputTokens },
    { id: 'chunks', header: 'Chunks', cell: (r: any) => r.chunkCount },
    { id: 'stopReason', header: 'Stop', cell: (r: any) => r.stopReason || '-' },
  ]

  const chartColumns = [
    { id: 'call', header: '#', cell: (_: any, i: number) => i + 1, width: 50 },
    { id: 'profile', header: 'Profile', cell: (r: any) => r.profileName },
    { id: 'ttft', header: 'TTFT (ms)', cell: (r: any) => Math.round(r.ttftSec * 1000) },
    { id: 'streaming', header: 'Streaming (ms)', cell: (r: any) => Math.round((r.totalTimeSec - r.ttftSec) * 1000) },
    { id: 'grIn', header: 'GR In (ms)', cell: (r: any) => r.guardrailInputLatencyMs > 0 ? Math.round(r.guardrailInputLatencyMs) : '-' },
    { id: 'grOut', header: 'GR Out (ms)', cell: (r: any) => r.guardrailOutputLatencyMs > 0 ? Math.round(r.guardrailOutputLatencyMs) : '-' },
    { id: 'total', header: 'Total (ms)', cell: (r: any) => Math.round(r.totalTimeSec * 1000) },
    { id: 'maxGap', header: 'Max Gap (ms)', cell: (r: any) => Math.round(r.maxGapSec * 1000) },
  ]

  const networkColumns = [
    { id: 'call', header: '#', cell: (_: any, i: number) => i + 1, width: 50 },
    { id: 'profile', header: 'Profile', cell: (r: any) => r.profileName },
    { id: 'sdkInit', header: 'SDK Init (ms)', cell: (r: any) => r.clientLatency?.sdkInitMs > 0 ? r.clientLatency.sdkInitMs.toFixed(2) : '-' },
    { id: 'reqBuild', header: 'Req Build (ms)', cell: (r: any) => r.clientLatency?.requestBuildMs > 0 ? r.clientLatency.requestBuildMs.toFixed(2) : '-' },
    { id: 'clientProc', header: 'Client Proc (ms)', cell: (r: any) => r.clientLatency?.clientProcessingMs > 0 ? r.clientLatency.clientProcessingMs.toFixed(2) : '-' },
    { id: 'rtOverhead', header: 'RT Overhead (ms)', cell: (r: any) => r.clientLatency?.roundTripOverheadMs > 0 ? Math.round(r.clientLatency.roundTripOverheadMs) : '-' },
    { id: 'brFirstByte', header: 'BR First Byte (ms)', cell: (r: any) => r.bedrockFirstByteLatencyMs > 0 ? Math.round(r.bedrockFirstByteLatencyMs) : '-' },
    { id: 'brInvocation', header: 'BR Invocation (ms)', cell: (r: any) => r.bedrockInvocationLatencyMs > 0 ? Math.round(r.bedrockInvocationLatencyMs) : '-' },
    { id: 'requestId', header: 'Request ID', cell: (r: any) => r.requestId ? <span style={{ fontSize: 11 }}>{r.requestId.slice(0, 12)}…</span> : '-' },
  ]

  const cols = columns === 'summary' ? summaryColumns : columns === 'charts' ? chartColumns : networkColumns
  const indexedCols = cols.map((c) => c.id === 'call'
    ? { ...c, cell: (item: any) => results.indexOf(item) + 1 }
    : c
  )

  return (
    <SpaceBetween size="m">
      <Table
        header={<Header variant="h3" counter={`(${successful.length})`}>Per-Call Data</Header>}
        columnDefinitions={indexedCols as any}
        items={successful}
        stripedRows
        stickyHeader
        variant="embedded"
        wrapLines
      />
      {failed.length > 0 && (
        <Table
          header={<Header variant="h3" counter={`(${failed.length})`}>Failed Calls</Header>}
          columnDefinitions={[
            { id: 'call', header: '#', cell: (item: any) => results.indexOf(item) + 1, width: 50 },
            { id: 'profile', header: 'Profile', cell: (r: any) => r.profileName },
            { id: 'error', header: 'Error', cell: (r: any) => r.error || 'Unknown error' },
          ]}
          items={failed}
          stripedRows
          variant="embedded"
        />
      )}
    </SpaceBetween>
  )
}

export function DetailedRunView({ runId, onBack }: Props) {
  const [activeTab, setActiveTab] = useState('summary')
  const { run, loading, error } = useRunData(runId)

  if (loading) {
    return (
      <Box textAlign="center" padding={{ vertical: 'xxl' }}>
        <Spinner size="large" />
        <Box variant="p" color="text-body-secondary" margin={{ top: 's' }}>
          Loading run data…
        </Box>
      </Box>
    )
  }

  if (error || !run) {
    return (
      <SpaceBetween size="m">
        <Alert type="error" header="Run not found">
          {error ?? `Could not find run with ID "${runId}".`}
        </Alert>
        <Button onClick={onBack} iconName="close">
          Close Details
        </Button>
      </SpaceBetween>
    )
  }

  const findings = generateFindings(run)

  return (
    <SpaceBetween size="l">
      <Header
        variant="h2"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => exportJson(run)} iconName="download">JSON</Button>
            <Button onClick={() => exportCsv(run)} iconName="download">CSV</Button>
            <Button onClick={onBack} iconName="close">Close</Button>
          </SpaceBetween>
        }
        description={`${new Date(run.startTime).toLocaleString()} — ${run.stats.length} profiles, ${run.results.length} calls`}
      >
        Run Details
      </Header>

      <Tabs
        activeTabId={activeTab}
        onChange={({ detail }) => setActiveTab(detail.activeTabId)}
        tabs={[
          {
            id: 'summary',
            label: 'Summary',
            content: (
              <SpaceBetween size="l">
                <SummaryTable stats={run.stats} results={run.results} />
                {findings.length > 0 && (
                  <Alert type="info" header="Key Findings">
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {findings.map((f, i) => (
                        <li key={i}>{f}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
                <CallDataTable results={run.results} columns="summary" />
              </SpaceBetween>
            ),
          },
          {
            id: 'charts',
            label: 'Latency',
            content: (
              <SpaceBetween size="l">
                <WaterfallChart run={run} />
                <HistogramPanel run={run} />
                <CallDataTable results={run.results} columns="charts" />
              </SpaceBetween>
            ),
          },
          {
            id: 'network',
            label: 'Network',
            content: <NetworkTab run={run} />,
          },
          {
            id: 'ai-analysis',
            label: 'AI Analysis',
            content: (
              <SpaceBetween size="l">
                <AnalysisPanel run={run} />
                <ChatPanel run={run} />
              </SpaceBetween>
            ),
          },
        ]}
      />
    </SpaceBetween>
  )
}
